import { useState } from 'react';
import type { Decision, RuleSet } from '@kkp-fraud/shared';
import type { RuleSetDraft } from '../hooks/useRuleSetDraft';
import { t } from '../i18n';
import { Dialog } from './Dialog';
import { Tip } from './PageHead';

/**
 * The set-level settings — Default and Fail-safe policy. They belong to
 * the whole document, not to a rule, so they open from the version line's "…" rather than
 * sitting beside the rule table.
 *
 * Product owner, 30 Sep: the score thresholds (step-up ≥ / decline ≥) are no longer shown or
 * edited here — there is no scoring; the document keeps its values untouched and nothing uses them. The fail-safe
 * policy is ONE action: the select writes it to both `cardPresentAction` and `ecommerceAction`
 * (the engine contract is unchanged; `ecommerceMinAmountThb` is left as it is). The select shows
 * `cardPresentAction`; Apply writes the chosen action to both fields.
 *
 * Product owner, 30 Sep (clean console): the threshold sign-off (`thresholdConfirmed`, "Confirm
 * all") and the note under the fail-safe select are gone. Rules keep `thresholdConfirmed` in the
 * document — the engine requires it — but nothing in the console shows or changes it.
 *
 * UX review P2-6: the dialog edits a copy. Nothing reaches the draft until "Apply to draft"
 * — which writes through `patchRuleSet`, exactly
 * the writes the form used to make on every change. Cancel, ✕, Escape and the scrim leave the
 * draft as it was. Only what actually changed is written, so an untouched draft stays clean.
 *
 * `readOnly` (a Viewer): every setting is shown, disabled; no Apply — Close.
 */

interface Form {
  defaultAction: RuleSet['defaultAction'];
  failSafe: RuleSet['failSafe'];
}

const DECISIONS: Decision[] = ['APPROVE', 'APPROVE_NOTIFY', 'STEP_UP', 'DECLINE'];

function formOf(draft: RuleSet): Form {
  return { defaultAction: draft.defaultAction, failSafe: { ...draft.failSafe } };
}

const sameJson = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

export function RuleSetSettingsDialog({ state, readOnly = false, onClose }: { state: RuleSetDraft; readOnly?: boolean; onClose: () => void }) {
  const draft = state.draft;
  const [form, setForm] = useState<Form | null>(() => (draft ? formOf(draft) : null));
  if (!draft || !form) return null;

  /** One fail-safe action for every transaction: both of the document's fields get it. */
  const setFailSafeAction = (action: Decision) =>
    setForm({ ...form, failSafe: { ...form.failSafe, cardPresentAction: action, ecommerceAction: action } });

  const patch: Partial<RuleSet> = {};
  if (form.defaultAction !== draft.defaultAction) patch.defaultAction = form.defaultAction;
  if (!sameJson(form.failSafe, draft.failSafe)) patch.failSafe = form.failSafe;
  const changed = Object.keys(patch).length > 0;

  const apply = () => {
    if (changed) state.patchRuleSet(patch);
    onClose();
  };

  return (
    <Dialog
      title={t('menu.settings.label')}
      plainBody
      onClose={onClose}
      testid="ruleset-settings"
      foot={
        readOnly ? (
          <>
            <span className="spacer" />
            <button type="button" className="btn" onClick={onClose}>
              {t('btn.close.label')}
            </button>
          </>
        ) : (
          <>
            <span className="note small muted">{t('settings.apply.note')}</span>
            <span className="spacer" />
            <button type="button" className="btn" onClick={onClose} data-testid="settings-cancel">
              {t('btn.cancel.label')}
            </button>
            <button type="button" className="btn primary" disabled={!changed} onClick={apply} data-testid="settings-apply">
              {t('settings.apply.action')}
            </button>
          </>
        )
      }
    >
      <section className="card">
        <header className="card-head">
          <h2>{t('default.panel.title')}</h2>
        </header>
        <div className="card-body stack">
          <label className="field">
            {t('default.action.label')}
            <select
              aria-label={t('default.action.a11y')}
              disabled={readOnly}
              value={form.defaultAction ?? 'APPROVE'}
              onChange={(e) => setForm({ ...form, defaultAction: e.target.value as Decision })}
            >
              {DECISIONS.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </label>
        </div>
      </section>

      <section className="card">
        <header className="card-head">
          <h2>{t('failsafe.panel.title')}</h2>
          <Tip>{t('failsafe.panel.note')}</Tip>
        </header>
        <div className="card-body stack">
          <label className="field">
            {t('failsafe.action.label')}
            <select
              aria-label={t('failsafe.action.a11y')}
              data-testid="failsafe-action"
              disabled={readOnly}
              value={form.failSafe.cardPresentAction}
              onChange={(e) => setFailSafeAction(e.target.value as Decision)}
            >
              {DECISIONS.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </label>
          {(form.failSafe.cardPresentAction === 'DECLINE' || form.failSafe.ecommerceAction === 'DECLINE') && (
            <div className="banner error small">{t('failsafe.decline.warning', { action: 'DECLINE' })}</div>
          )}
        </div>
      </section>

    </Dialog>
  );
}
